import { createObjectCsvStringifier } from "csv-writer";
import _ from "lodash";

import { eventCategories, eventDefType } from "../eventTypes";

import { allDestinationsData } from "../destinations/destinations";
import { LoadedEventDefinition } from "../types/global";

const eventCategoriesMap = _.keyBy(eventCategories, "type");

const getEventCategory = (eventDef: LoadedEventDefinition) => {
  const type = eventDefType(eventDef);
  const eventCategoryObj = eventCategoriesMap[type];
  if (eventCategoryObj && "name" in eventCategoryObj) {
    if (eventCategoryObj.name === "Pageviews") {
      return "Pageview";
    }
    if (eventCategoryObj.name === "Clicks") {
      return "Click";
    }
    return eventCategoryObj.name;
  }
  return "";
};

function getDestinationName(event: LoadedEventDefinition, destinations) {
  const destinationIds = Object.keys(event.enabled_dests).filter((id) => {
    return event.enabled_dests[id];
  });
  const eventDestinationsMap = _.keyBy(destinations, "id");

  const eventDestinationNames = destinationIds.map((destinationId) => {
    const config = eventDestinationsMap[destinationId];
    const dest = allDestinationsData[config.type_slug];

    if (!dest) {
      return destinationId;
    }

    let name = dest.name;
    if (dest.supportsMultiConfig) {
      name += ` | ${dest.multiConfigLabel}: ${config.instance_name}`;
    }
    return name;
  });

  return eventDestinationNames.sort();
}

function getCSV(events: LoadedEventDefinition[], destinations) {
  const csvStringifier = createObjectCsvStringifier({
    header: [
      { id: "id", title: "Event ID" },
      { id: "name", title: "Event Name" },
      { id: "description", title: "Event Description" },
      { id: "type", title: "Event Type" },
      { id: "destinations", title: "Event Destinations" },
      { id: "filters", title: "Event Filters" },
      { id: "dynamic_properties", title: "Event Dynamic Properties" },
      { id: "created_at", title: "Created At" },
      { id: "updated_at", title: "Updated At" },
      { id: "tags", title: "Tags" },
    ],
  });

  const records = events.map((event) => ({
    id: event.id,
    name: event.name ?? "",
    description: event.description,
    type: getEventCategory(event),
    destinations: event.segment_compatible
      ? "All Destinations"
      : getDestinationName(event, destinations),
    filters:
      event.source === "autotrack" && event.type !== "manual_tracked"
        ? JSON.stringify(event.filter.filters.slice(1), null)
        : null,
    dynamic_properties:
      event.source === "autotrack" && event.type !== "manual_tracked"
        ? JSON.stringify(event.snapshots, null)
        : null,
    created_at: event.created_at,
    updated_at: event.updated_at,
    tags: JSON.stringify(Object.keys(event.tags).sort()),
  }));

  const headerString = csvStringifier.getHeaderString();

  const recordsString = csvStringifier.stringifyRecords(records);

  return headerString + recordsString;
}

export function downloadEventSchema(
  events: LoadedEventDefinition[],
  destinations
) {
  const csvData = getCSV(events, destinations);
  var element = document.createElement("a");
  element.setAttribute(
    "href",
    "data:text/plain;charset=utf-8," + encodeURIComponent(csvData)
  );
  element.setAttribute("download", "events_schema.csv");

  element.style.display = "none";
  document.body.appendChild(element);

  element.click();

  document.body.removeChild(element);
}
